// Template 3 — "Spotlight" — a cinematic premium landing page: near-black canvas,
// a cursor-follow spotlight glow, film-grain texture, huge Space Grotesk display,
// a wide cinematic video, a timeline curriculum and warm amber accent. Fully
// theme-driven off the branding color.
import { Fragment, useEffect, useRef, useState } from "react";
import RichText from "../RichText";
import { Icon, Stars, splitTitle, rgba, shade, useGoogleFonts, usePreviewVideo, VideoModal, HeroVideo, SOCIAL_ICONS, orderedSections, skillIconD, PolicyLinks, GuaranteeBadge, Reveal, LessonIcon } from "./shared";

const SIGNATURE2 = "#5B7CFA";
const SIGNATURE = "#F5A623";     // warm amber — cinematic glow on black
const BG = "#0a0a0c";            // near-black canvas
const CARD = "#141418";          // dark card
const FOOT = "#070708";          // footer
const LINE = "rgba(255,255,255,.08)";
const MUT = "rgba(245,245,246,.55)";
const DISPLAY = "'Space Grotesk', system-ui, sans-serif";
const SANS = "'Inter', system-ui, sans-serif";

export default function Template3({ course, landing, instructor, modules, themeColor, themeColor2, canView, labels = {}, onEnroll, policies }) {
  useGoogleFonts("https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600&display=swap");
  const vid = usePreviewVideo();
  const A = themeColor || SIGNATURE;
  const AD = shade(A, -0.2);
  const AL = shade(A, 0.4);
  const B = themeColor2 || SIGNATURE2;   // secondary branding accent
  const grad = `linear-gradient(135deg, ${A}, ${AD})`;
  const pal = { A, AD, AL, B, grad };

  const header = landing.header || {};
  const t = splitTitle(header.headline || course.name);
  const sub = header.subheadline || course.short_description || "";
  const isPaid = course.access_type === 2;
  const priceLabel = isPaid && course.price > 0 ? course.price : (labels.free || "Free");
  const ctaLabel = isPaid ? (labels.buy_now || "Buy Now") : (labels.enroll_now || "Enroll Now");
  const statusLabel = isPaid ? "Premium course" : course.access_type === 3 ? (canView ? "Unlocked" : "Members only") : "Free course";
  const lessonCount = course.total_lessons || modules.reduce((a, m) => a + (m.lessons?.length || 0), 0);

  const order = orderedSections(landing.section_order);
  const navLinks = [
    landing.about?.show !== false && (landing.description || course.description) && { href: "#t3-about", label: "About" },
    landing.skill?.show && landing.skill.list?.length > 0 && { href: "#t3-skill", label: "Skills" },
    modules.length > 0 && landing.course_include?.show !== false && { href: "#t3-curriculum", label: labels.curriculum || "Curriculum" },
    instructor?.name && landing.instructor?.show !== false && { href: "#t3-instructor", label: "Instructor" },
    landing.testimonials?.show && landing.testimonials.list?.length > 0 && { href: "#t3-reviews", label: labels.reviews || "Reviews" },
    landing.faq?.show && landing.faq.list?.length > 0 && { href: "#t3-faq", label: labels.faq || "FAQ" },
  ].filter(Boolean);

  const stats = [
    course.star_rating > 0 && { v: Number(course.star_rating).toFixed(1), k: labels.rating || "Rating" },
    course.total_lessons > 0 && { v: course.total_lessons, k: labels.lessons || "Lessons" },
    modules.length > 0 && { v: modules.length, k: labels.modules || "Modules" },
    { v: "∞", k: "Lifetime" },
  ].filter(Boolean);

  const blocks = {
    about: landing.about?.show !== false && (landing.description || course.description) && (
      <Reveal><Sec id="t3-about" index="01" kicker="Overview" title="About this course" pal={pal}>
        <div className="rich-content mt-6 max-w-3xl text-[15px] leading-relaxed" style={{ color: MUT }}><RichText html={landing.description || course.description} /></div>
      </Sec></Reveal>
    ),
    instructor: landing.instructor?.show !== false && instructor?.name && (
      <Reveal><div id="t3-instructor"><Sec index="02" kicker="Instructor" title={labels.meet_instructor || "Meet the instructor"} pal={pal}>
        <div className="relative mt-8 flex flex-col items-center gap-8 overflow-hidden rounded-3xl border p-8 sm:flex-row" style={{ background: CARD, borderColor: LINE }}>
          <div className="pointer-events-none absolute -left-20 -top-20 h-56 w-56 rounded-full opacity-40 blur-[80px]" style={{ background: rgba(A, 0.4) }} />
          <span className="relative flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-2xl text-4xl font-bold text-white" style={{ background: BG, border: `1px solid ${rgba(A, 0.4)}`, boxShadow: `0 0 40px ${rgba(A, 0.25)}`, fontFamily: DISPLAY }}>{instructor.profile_image ? <img src={instructor.profile_image} alt="" className="h-full w-full object-cover" /> : instructor.name.charAt(0).toUpperCase()}</span>
          <div className="relative text-center sm:text-left">
            <h3 className="text-2xl font-bold text-white" style={{ fontFamily: DISPLAY }}>{instructor.name}</h3>
            <p className="mt-1 text-[0.72rem] font-semibold uppercase tracking-[0.18em]" style={{ color: A }}>Course Instructor</p>
            {instructor.bio && <p className="mt-3 max-w-lg text-[15px] leading-relaxed" style={{ color: MUT }}>{instructor.bio}</p>}
          </div>
        </div>
      </Sec></div></Reveal>
    ),
    skill: landing.skill?.show && landing.skill.list?.length > 0 && (
      <Reveal><Sec id="t3-skill" index="03" kicker={labels.what_youll_learn || "What you'll learn"} title={landing.skill.heading || "Skills you will walk away with"} pal={pal}>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {landing.skill.list.map((s, i) => (
            <div key={i} className="group flex items-center gap-4 rounded-2xl border px-5 py-4 transition-all duration-300 hover:border-white/20" style={{ background: CARD, borderColor: LINE }}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-all duration-300 group-hover:scale-110" style={{ background: rgba(A, 0.14), color: A, boxShadow: `inset 0 0 0 1px ${rgba(A, 0.25)}` }}><Icon d={skillIconD(landing.skill.icon)} className="h-4 w-4" stroke={2.2} /></span>
              <span className="text-[15px] font-medium text-white/85">{s}</span>
            </div>
          ))}
        </div>
        {landing.skill.long_desc && <div className="rich-content mt-6 max-w-3xl text-[15px] leading-relaxed" style={{ color: MUT }} dangerouslySetInnerHTML={{ __html: landing.skill.long_desc }} />}
      </Sec></Reveal>
    ),
    include: modules.length > 0 && landing.course_include?.show !== false && (
      <Reveal><div id="t3-curriculum"><Sec index="04" kicker={labels.curriculum || "Curriculum"} title={labels.what_inside_course || "What's inside the course"} sub={`${modules.length} ${labels.modules || "Modules"} · ${lessonCount} ${labels.lessons || "Lessons"}`} pal={pal}>
        <Timeline modules={modules} pal={pal} labels={labels} />
      </Sec></div></Reveal>
    ),
    testimonial: <div id="t3-reviews"><Testimonials testimonials={landing.testimonials} pal={pal} labels={labels} /></div>,
    moneyback: isPaid && <Guarantee guarantee={landing.guarantee} pal={pal} />,
    faq: <div id="t3-faq"><Faq faq={landing.faq} pal={pal} labels={labels} /></div>,
  };

  return (
    <div className="relative min-h-screen text-white" style={{ background: BG, fontFamily: SANS }}>
      <Grain />
      {/* NAV */}
      <nav className="sticky top-0 z-50 border-b backdrop-blur-xl" style={{ background: rgba(BG, 0.7), borderColor: LINE }}>
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a href="#top" className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg text-black" style={{ background: A, boxShadow: `0 0 20px ${rgba(A, 0.5)}` }}><Icon d="M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.42a12 12 0 01.34 3.42c0 3-3 5-6.5 5S5.5 17 5.5 14a12 12 0 01.34-3.42L12 14z" className="h-4 w-4" /></span>
            <span className="max-w-[40vw] truncate text-[15px] font-bold tracking-tight text-white sm:max-w-xs" style={{ fontFamily: DISPLAY }}>{course.name}</span>
          </a>
          <div className="hidden items-center gap-7 md:flex">
            {navLinks.map((l) => <a key={l.href} href={l.href} className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] transition hover:text-white" style={{ color: MUT }}>{l.label}</a>)}
          </div>
          <button onClick={onEnroll} className="group relative overflow-hidden rounded-full px-5 py-2 text-sm font-bold text-black transition-transform duration-200 hover:-translate-y-0.5" style={{ background: A, boxShadow: `0 0 24px ${rgba(A, 0.5)}` }}>
            <Shine /> <span className="relative">{ctaLabel}</span>
          </button>
        </div>
      </nav>

      {/* HERO — cinematic centered */}
      <header id="top" className="relative overflow-hidden">
        <Spotlight accent={A} accent2={B} />
        <div className="pointer-events-none absolute inset-0 opacity-[0.5]" style={{ backgroundImage: `linear-gradient(${rgba(A, 0.05)} 1px, transparent 1px), linear-gradient(90deg, ${rgba(A, 0.05)} 1px, transparent 1px)`, backgroundSize: "60px 60px", maskImage: "radial-gradient(70% 60% at 50% 0%, #000, transparent 75%)", WebkitMaskImage: "radial-gradient(70% 60% at 50% 0%, #000, transparent 75%)" }} />
        {header.bg_image ? <img src={header.bg_image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-15" /> : null}
        <div className="relative mx-auto max-w-4xl px-4 pt-16 text-center sm:px-6 lg:px-8 lg:pt-24">
          <span className="inline-flex animate-fade-in-up items-center gap-2 rounded-full border px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: A, borderColor: rgba(A, 0.3), background: rgba(A, 0.08) }}>
            <span className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70" style={{ background: A }} /><span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ background: A }} /></span>
            {statusLabel}
          </span>
          <h1 className="mx-auto mt-6 max-w-3xl animate-fade-in-up text-[2.9rem] font-[700] leading-[0.98] tracking-[-0.03em] text-white sm:text-6xl lg:text-[4.4rem]" style={{ fontFamily: DISPLAY, animationDelay: "60ms" }}>
            {t.before}{" "}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(120deg, ${A}, ${AL})`, filter: `drop-shadow(0 0 24px ${rgba(A, 0.4)})` }}>{t.accent}</span>{" "}
            {t.after}
          </h1>
          {sub && <p className="mx-auto mt-6 max-w-xl animate-fade-in-up text-[16px] leading-relaxed sm:text-[17px]" style={{ color: MUT, animationDelay: "120ms" }}>{sub}</p>}
          <div className="mt-8 flex animate-fade-in-up flex-wrap items-center justify-center gap-3" style={{ animationDelay: "180ms" }}>
            <button onClick={onEnroll} className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full px-7 py-3.5 text-[15px] font-bold text-black transition-transform duration-200 hover:-translate-y-0.5" style={{ background: A, boxShadow: `0 0 40px ${rgba(A, 0.5)}` }}>
              <Shine /> <span className="relative">{ctaLabel}</span> <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="relative h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a href="#t3-curriculum" className="inline-flex items-center gap-2 rounded-full border px-6 py-3.5 text-[15px] font-semibold text-white/85 transition hover:bg-white/5" style={{ borderColor: LINE }}>{labels.preview || "Preview Course"}</a>
          </div>
        </div>

        {/* wide cinematic video */}
        {landing.header?.show !== false && (
          <div className="relative mx-auto mt-14 max-w-5xl animate-fade-in-up px-4 sm:px-6 lg:px-8" style={{ animationDelay: "220ms" }}>
            <div className="pointer-events-none absolute -inset-8 -z-10 rounded-[40px] opacity-70 blur-3xl" style={{ background: `radial-gradient(50% 60% at 50% 40%, ${rgba(A, 0.35)}, transparent 70%)` }} />
            <div className="overflow-hidden rounded-[1.5rem] border p-1.5" style={{ background: CARD, borderColor: rgba(A, 0.25), boxShadow: `0 40px 100px -30px ${rgba(A, 0.35)}` }}>
              <HeroVideo video={landing.video} poster={course.course_image} accent={A} className="aspect-video w-full rounded-[1.15rem]" style={{ background: "#000" }} onExpand={vid.play} premium />
            </div>
          </div>
        )}

        {/* stats strip */}
        <div className="relative mx-auto mt-14 grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-3xl border sm:grid-cols-4" style={{ borderColor: LINE, background: LINE }}>
          {stats.map((s, i) => (
            <div key={i} className="px-6 py-6 text-center" style={{ background: BG }}>
              <div className="text-3xl font-[700] tracking-tight sm:text-4xl" style={{ fontFamily: DISPLAY, color: A }}>{s.v}</div>
              <div className="mt-1 text-[0.7rem] font-semibold uppercase tracking-[0.14em]" style={{ color: MUT }}>{s.k}</div>
            </div>
          ))}
        </div>
      </header>

      {/* BODY */}
      <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        {/* ENROLL PANEL */}
        <Reveal>
          <div className="mb-20 overflow-hidden rounded-3xl border" style={{ background: CARD, borderColor: LINE }}>
            <div className="grid gap-6 p-6 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:gap-9 sm:p-8">
              <div className="text-center sm:border-r sm:pr-9 sm:text-left" style={{ borderColor: LINE }}>
                <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: A }}>{statusLabel}</span>
                <div className="mt-1 text-5xl font-[700] leading-none tracking-tight" style={{ fontFamily: DISPLAY, color: A }}>{priceLabel}{isPaid && <span className="ml-2 text-sm font-normal text-white/40">one-time</span>}</div>
              </div>
              <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2.5">
                {[course.total_lessons > 0 && `${course.total_lessons} ${labels.lessons || "lessons"}`, modules.length > 0 && `${modules.length} ${labels.modules || "modules"}`, "Lifetime access", "Learn on any device"].filter(Boolean).map((it, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm font-medium text-white/70"><Icon d="M4.5 12.75l6 6 9-13.5" className="h-4 w-4" stroke={2.4} style={{ color: A }} />{it}</li>
                ))}
              </ul>
              <button onClick={onEnroll} className="group relative flex items-center justify-center gap-2 overflow-hidden rounded-full px-8 py-4 text-sm font-bold text-black transition-transform duration-200 hover:-translate-y-0.5" style={{ background: A, boxShadow: `0 0 34px ${rgba(A, 0.45)}` }}>
                <Shine /> <span className="relative">{ctaLabel}</span> <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="relative h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
            {isPaid && landing.guarantee?.show && (
              <div className="border-t px-6 py-3.5 text-center text-[13px] sm:px-8" style={{ borderColor: LINE, background: rgba(A, 0.06) }}>
                <span className="font-bold text-white">{landing.guarantee.days || 30}-Day {labels.money_back_guarantee || "Money-Back Guarantee"}.</span> <span className="text-white/60">{labels.not_satisfied || "Not satisfied? Get a full refund."}</span>
              </div>
            )}
          </div>
        </Reveal>

        {/* SECTIONS — full width */}
        <div className="space-y-24">
          {order.map((id) => <Fragment key={id}>{blocks[id]}</Fragment>)}
        </div>

        {/* FINAL CTA */}
        {landing.cta?.show !== false && (
          <Reveal>
            <section className="relative mt-20 overflow-hidden rounded-[2.5rem] border px-6 py-20 text-center sm:px-12" style={{ background: CARD, borderColor: rgba(A, 0.2) }}>
              <div className="pointer-events-none absolute inset-0 opacity-80" style={{ background: `radial-gradient(60% 100% at 50% 0%, ${rgba(A, 0.3)}, transparent 60%)` }} />
              <div className="pointer-events-none absolute inset-0 opacity-[0.3]" style={{ backgroundImage: `linear-gradient(${rgba(A, 0.06)} 1px, transparent 1px), linear-gradient(90deg, ${rgba(A, 0.06)} 1px, transparent 1px)`, backgroundSize: "48px 48px" }} />
              <div className="relative">
                <h2 className="text-3xl font-[700] tracking-tight text-white sm:text-[3rem]" style={{ fontFamily: DISPLAY }}>
                  {landing.cta?.heading ? landing.cta.heading : <>{t.before} <span style={{ color: A }}>{t.accent}</span> {t.after}</>}
                </h2>
                <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-white/60">{landing.cta?.subheading || sub || `Join ${course.name} and start learning today.`}</p>
                <button onClick={onEnroll} className="group relative mt-8 inline-flex items-center gap-2 overflow-hidden rounded-full px-8 py-4 text-[15px] font-bold text-black transition-transform duration-200 hover:-translate-y-0.5" style={{ background: A, boxShadow: `0 0 44px ${rgba(A, 0.55)}` }}>
                  <Shine /> <span className="relative">{ctaLabel}</span> <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="relative h-4 w-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </section>
          </Reveal>
        )}
      </div>

      {/* FOOTER */}
      <footer className="relative border-t" style={{ background: FOOT, borderColor: LINE }}>
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-5 px-4 py-10 sm:flex-row sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5">
            {Object.keys(SOCIAL_ICONS).filter((k) => landing.social?.[k]).map((k) => (
              <a key={k} href={landing.social[k]} target="_blank" rel="noopener noreferrer" aria-label={k} className="flex h-9 w-9 items-center justify-center rounded-lg text-white/70 transition hover:-translate-y-0.5 hover:text-black" style={{ background: "rgba(255,255,255,.07)" }} onMouseEnter={(e) => { e.currentTarget.style.background = A; }} onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,.07)"; }}>
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d={SOCIAL_ICONS[k]} /></svg>
              </a>
            ))}
          </div>
          <PolicyLinks policies={policies} accent={A} dark />
        </div>
      </footer>

      <VideoModal video={landing.video} open={vid.open} onClose={vid.close} />
    </div>
  );
}

/* -------------------------------- helpers -------------------------------- */
function guaranteeDefaultHtml(days) {
  return `<p>We believe in the value of this course and are confident it will help you achieve your goals. That's why we offer a full ${days}-day risk-free money-back guarantee.</p><p>Go through the content, explore the lessons, and put what you learn into action. If at any point within the first ${days} days you feel the course isn't right for you or doesn't meet your expectations, simply reach out to us.</p><p>We'll process your refund promptly—no questions asked.</p><p>There's absolutely no risk in getting started today, only the opportunity to learn, grow, and succeed with complete peace of mind.</p>`;
}
function Shine() {
  return <span className="pointer-events-none absolute inset-y-0 -left-4 w-1/3 animate-shine bg-white/30 blur-md" aria-hidden />;
}
// Film-grain overlay (fixed) for a cinematic texture.
function Grain() {
  const noise = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E";
  return <div className="pointer-events-none fixed inset-0 z-[1] opacity-[0.045] mix-blend-screen" style={{ backgroundImage: `url("${noise}")` }} />;
}
// Radial spotlight that follows the cursor across the hero, trailed by a
// secondary-color glow for a dual-tone cinematic wash.
function Spotlight({ accent, accent2 }) {
  const ref = useRef(null);
  const [p, setP] = useState({ x: 50, y: 30 });
  useEffect(() => {
    const el = ref.current?.parentElement;
    if (!el) return;
    const onMove = (e) => { const r = el.getBoundingClientRect(); setP({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 }); };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);
  return <div ref={ref} className="pointer-events-none absolute inset-0 transition-[background] duration-300 ease-out" style={{ background: `radial-gradient(600px circle at ${p.x}% ${p.y}%, ${rgba(accent, 0.16)}, transparent 55%), radial-gradient(520px circle at ${p.x + 12}% ${p.y + 16}%, ${rgba(accent2, 0.1)}, transparent 55%)` }} />;
}

function Sec({ id, index, kicker, title, sub, pal, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="flex items-baseline gap-4">
        <span className="text-sm font-[700] tabular-nums" style={{ fontFamily: DISPLAY, color: rgba(pal.B, 0.75) }}>{index}</span>
        <div>
          <span className="text-[0.7rem] font-bold uppercase tracking-[0.2em]" style={{ color: pal.A }}>{kicker}</span>
          <h2 className="mt-1.5 text-[2rem] font-[700] leading-[1.04] tracking-[-0.02em] text-white sm:text-[2.6rem]" style={{ fontFamily: DISPLAY }}>{title}</h2>
          {sub && <p className="mt-2 text-[15px] text-white/50">{sub}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function Timeline({ modules, pal, labels }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="relative mt-8 pl-9">
      <span className="absolute bottom-4 left-3 top-4 w-px" style={{ background: `linear-gradient(to bottom, ${rgba(pal.B, 0.5)}, ${rgba(pal.A, 0.35)} 45%, ${LINE})` }} />
      {modules.map((m, mi) => {
        const isOpen = open === mi;
        return (
          <div key={mi} className="relative pb-3">
            <span className="absolute -left-9 top-3.5 flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold transition" style={isOpen ? { background: pal.A, color: "#000", boxShadow: `0 0 18px ${rgba(pal.A, 0.55)}` } : { background: CARD, color: pal.A, border: `1px solid ${rgba(pal.A, 0.35)}` }}>{mi + 1}</span>
            <div className="overflow-hidden rounded-2xl border transition-all duration-300" style={isOpen ? { background: CARD, borderColor: rgba(pal.A, 0.35) } : { background: CARD, borderColor: LINE }}>
              <button onClick={() => setOpen(isOpen ? -1 : mi)} className="flex w-full items-center gap-4 px-5 py-4 text-left">
                <span className="flex-1 text-[15px] font-bold text-white" style={{ fontFamily: DISPLAY }}>{m.name}</span>
                <span className="hidden text-xs font-medium text-white/45 sm:block">{m.lessons?.length || 0} {labels.lessons || "lessons"}</span>
                <Icon d="M19 9l-7 7-7-7" className={`h-4 w-4 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} style={{ color: isOpen ? pal.A : "rgba(255,255,255,.4)" }} />
              </button>
              <div className="grid transition-all duration-300" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
                <div className="overflow-hidden">
                  <ul className="px-5 pb-2">
                    {(m.lessons || []).map((l, li) => (
                      <li key={li} className="flex items-center gap-3 border-t py-3" style={{ borderColor: LINE }}>
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ background: l.locked ? "rgba(255,255,255,.06)" : rgba(pal.A, 0.14) }}>
                          <LessonIcon lesson={l} color={pal.A} muted="rgba(255,255,255,.35)" className="h-3.5 w-3.5" />
                        </span>
                        <span className={`flex-1 truncate text-sm ${l.locked ? "text-white/40" : "text-white/80"}`}>{l.title}</span>
                        {l.completed && <span className="text-xs font-semibold" style={{ color: pal.A }}>Done</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Testimonials({ testimonials, pal, labels }) {
  const list = (testimonials?.list || []).filter((t) => t.quote || t.name);
  if (!testimonials?.show || !list.length) return null;
  return (
    <Reveal>
      <Sec index="05" kicker={labels.testimonials || "Testimonials"} title={testimonials.heading || "What students are saying"} pal={pal}>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {list.map((t, i) => (
            <figure key={i} className="group relative flex flex-col overflow-hidden rounded-3xl border p-6 transition-all duration-300 hover:border-white/20" style={{ background: CARD, borderColor: LINE }}>
              <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full opacity-0 blur-3xl transition-opacity duration-300 group-hover:opacity-100" style={{ background: rgba(pal.A, 0.25) }} />
              <Stars value={t.rating} empty="rgba(255,255,255,.16)" color={pal.A} />
              <blockquote className="relative mt-4 flex-1 text-[15px] leading-relaxed text-white/75">{t.quote}</blockquote>
              <figcaption className="relative mt-6 flex items-center gap-3 border-t pt-5" style={{ borderColor: LINE }}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full text-sm font-bold text-black" style={{ background: pal.A }}>{t.avatar ? <img src={t.avatar} alt="" className="h-full w-full object-cover" /> : (t.name || "?").charAt(0).toUpperCase()}</span>
                <span className="min-w-0"><span className="block truncate text-sm font-bold text-white">{t.name}</span>{t.role && <span className="block truncate text-xs text-white/45">{t.role}</span>}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </Sec>
    </Reveal>
  );
}

function Guarantee({ guarantee, pal }) {
  if (!guarantee?.show) return null;
  const days = guarantee.days || 30;
  return (
    <Reveal>
      <section className="relative overflow-hidden rounded-3xl border px-6 py-11 sm:px-10" style={{ background: CARD, borderColor: rgba(pal.A, 0.2) }}>
        <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(60% 120% at 88% 50%, ${rgba(pal.A, 0.28)}, transparent 60%)` }} />
        {guarantee.bg_image ? <img src={guarantee.bg_image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20" /> : null}
        <div className="relative flex flex-col items-center gap-8 text-center sm:flex-row sm:text-left">
          <div className="order-2 flex-1 sm:order-1">
            <h3 className="text-2xl font-[700] text-white sm:text-[1.9rem]" style={{ fontFamily: DISPLAY }}>{guarantee.heading || `${days}-Day Risk-Free Money-Back Guarantee`}</h3>
            <div className="rich-content mt-3 text-[15px] leading-relaxed text-white/65" dangerouslySetInnerHTML={{ __html: guarantee.description || guaranteeDefaultHtml(days) }} />
          </div>
          <div className="order-1 shrink-0 sm:order-2">
            {guarantee.image
              ? <img src={guarantee.image} alt="" className="h-44 w-44 object-contain drop-shadow-2xl" />
              : <GuaranteeBadge variant={guarantee.badge || 1} days={days} accent={pal.A} className="h-44 w-44 drop-shadow-2xl sm:h-48 sm:w-48" />}
          </div>
        </div>
      </section>
    </Reveal>
  );
}

function Faq({ faq, pal, labels }) {
  const list = (faq?.list || []).filter((f) => f.q);
  const [open, setOpen] = useState(-1);
  if (!faq?.show || !list.length) return null;
  return (
    <Reveal>
      <Sec index="06" kicker={labels.faq || "FAQ"} title={faq.heading || "Frequently asked questions"} pal={pal}>
        <div className="mt-8 space-y-3">
          {list.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="overflow-hidden rounded-2xl border transition-all duration-300" style={isOpen ? { background: CARD, borderColor: rgba(pal.A, 0.35) } : { background: CARD, borderColor: LINE }}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} className="flex w-full items-center gap-4 px-5 py-4 text-left">
                  <span className="flex-1 text-[15px] font-bold text-white" style={{ fontFamily: DISPLAY }}>{f.q}</span>
                  <Icon d="M12 4v16m8-8H4" className={`h-4 w-4 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`} style={{ color: pal.A }} />
                </button>
                <div className="grid transition-all duration-300" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
                  <div className="overflow-hidden"><p className="px-5 pb-5 text-[15px] leading-relaxed text-white/70">{f.a}</p></div>
                </div>
              </div>
            );
          })}
        </div>
      </Sec>
    </Reveal>
  );
}
